import { graphql } from 'gatsby';

import { Grid2 as Grid, styled } from '@mui/material';

import { LabeledNumber as LabeledNumberUI, ResponsiveGrid } from '@mewa/ui';

import { ComponentProps, PossibleContentSections } from '../fragment-and-types';
import { StrapiImage, StrapiResponsiveImageData } from './image';
import { LabeledNumber } from './labeled-number';

export const ImageSlideWithNumbersFragment = graphql`
  fragment ImageSlideFragment on STRAPI__COMPONENT_SHARED_IMAGE_SLIDE {
    title
    subtitle
    imageDesktop {
      ...StrapiImageFileFragment
    }
    imageMobile {
      ...StrapiImageFileFragment
    }
    imageAlternativeText
    strapi_component
  }

  fragment ImageSlideWithNumbersFragment on STRAPI__COMPONENT_SHARED_IMAGE_SLIDE_WITH_NUMBERS {
    strapi_component
    image {
      ...ImageSlideFragment
    }
    numbers {
      ...LabeledNumberFragment
    }
    link {
      text
      url
    }
  }
`;

export const componentName = 'shared.image-slide-with-numbers';

export type ImageSlide = StrapiResponsiveImageData & {
  title: string | null;
  subtitle: string | null;
};

export type Link = {
  text: string;
  url: string;
};

export type ImageSlideWithNumbersData = {
  strapi_component: typeof componentName;
} & {
  image: ImageSlide;
  numbers: LabeledNumber[];
  link?: Link;
};

export const isImageSlideWithNumbers = (
  component: PossibleContentSections,
): component is ImageSlideWithNumbersData => {
  return component.strapi_component === componentName;
};

// -----------------------------------------------------------------------------
// Single Image With Numbers
// -----------------------------------------------------------------------------

export const StrapiImageWithNumbers = ({
  locale,
  image,
  numbers,
}: ComponentProps<ImageSlideWithNumbersData>) => {
  return (
    <ResponsiveGrid sx={{ marginY: ['58px', '140px'] }}>
      <Grid
        size={{
          xs: 12,
          md: 8,
        }}
      >
        <StrapiImage responsiveImage={image} />
      </Grid>
      <Grid
        offset={{ md: 1 }}
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <NumberGridContainer>
          <Grid container>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 12,
              }}
            >
              {numbers.length > 0 && (
                <LabeledNumberUI
                  number={numbers[0].number}
                  label={numbers[0].label}
                  suffix={numbers[0].suffix}
                  locale={locale}
                />
              )}
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 12,
              }}
            >
              {numbers.length > 1 && (
                <LabeledNumberUI
                  sx={{ marginTop: { md: '70px', xs: '42px', sm: 0 } }}
                  number={numbers[1].number}
                  label={numbers[1].label}
                  suffix={numbers[1].suffix}
                  locale={locale}
                />
              )}
            </Grid>
          </Grid>
        </NumberGridContainer>
      </Grid>
    </ResponsiveGrid>
  );
};

const NumberGridContainer = styled('div')(({ theme }) => ({
  // align large numbers with top image border
  marginTop: '-16px',

  [theme.breakpoints.down('md')]: {
    marginTop: '25px',
  },
}));

export const config = {
  isComponent: isImageSlideWithNumbers,
  Component: StrapiImageWithNumbers,
  componentName,
};
